/**
 * Coverage tracker for simulation v2 (spec §10).
 *
 * Accumulates entity/CF hit counts across multiple simulate() calls,
 * then produces a CoverageSnapshot for the CoverageReport view.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import type { CoverageSnapshot, EnrichedSimulationTrace } from './simulator.js';

export class CoverageTracker {
  private entityHits = new Map<string, number>();
  private cfHits = new Map<string, number>();
  private manifest: AtlasManifest;
  private overlayManifest: AtlasManifest | undefined;

  constructor(manifest: AtlasManifest, overlayManifest?: AtlasManifest) {
    this.manifest = manifest;
    this.overlayManifest = overlayManifest;
  }

  accumulate(trace: EnrichedSimulationTrace): void {
    for (const step of trace.steps) {
      for (const entityId of step.writes) {
        this.entityHits.set(entityId, (this.entityHits.get(entityId) ?? 0) + 1);
      }
      for (const cfName of step.cfsInvoked) {
        this.cfHits.set(cfName, (this.cfHits.get(cfName) ?? 0) + 1);
      }
    }
  }

  snapshot(): CoverageSnapshot {
    // Use overlay entities if provided, otherwise use base manifest
    const workingManifest = this.overlayManifest ?? this.manifest;

    const allEntityIds = workingManifest.entities.map((e) => e.id);
    const allCfNames = workingManifest.cloudFunctions.map((cf) => cf.name);

    const entityHits = allEntityIds
      .map((entityId) => ({ entityId, count: this.entityHits.get(entityId) ?? 0 }))
      .sort((a, b) => b.count - a.count);

    const cfHits = allCfNames
      .map((cfName) => ({ cfName, count: this.cfHits.get(cfName) ?? 0 }))
      .sort((a, b) => b.count - a.count);

    const domainCoverage = workingManifest.domains.map((domain) => {
      const domainEntities = workingManifest.entities.filter((e) => e.domain === domain.id);
      const hitCount = domainEntities.filter((e) => (this.entityHits.get(e.id) ?? 0) > 0).length;
      const totalCount = domainEntities.length;
      return {
        domainId: domain.id,
        percent: totalCount === 0 ? 0 : hitCount / totalCount,
        hitCount,
        totalCount,
      };
    });

    const darkZones = allEntityIds.filter((id) => !this.entityHits.has(id));
    const touchedCount = allEntityIds.filter((id) => this.entityHits.has(id)).length;
    const totalEntityCoverage = allEntityIds.length === 0 ? 0 : touchedCount / allEntityIds.length;

    return { entityHits, cfHits, domainCoverage, darkZones, totalEntityCoverage };
  }

  reset(): void {
    this.entityHits.clear();
    this.cfHits.clear();
  }
}
